import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  CreditCard,
  Clock,
  Shield,
  LogOut,
  Upload,
  Brain,
  AlertTriangle,
} from 'lucide-react';

interface AccountDetail {
  id: string;
  email: string;
  displayName: string | null;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  failedLoginCount: number;
  lockedUntil: string | null;
  stripeCustomerId: string | null;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  createdAt: string;
  updatedAt: string;
  _count: {
    sessions: number;
    ouraImports: number;
    ouraDailySummaries: number;
    ouraSleepSessions: number;
    ouraWorkouts: number;
    ouraGrokAnalyses: number;
  };
}

interface BillingEvent {
  id: string;
  type: string;
  stripeEventId: string;
  createdAt: string;
}

interface AuditEvent {
  id: string;
  action: string;
  resource: string;
  resourceId: string | null;
  status: string;
  ipAddress: string | null;
  createdAt: string;
  metadata: any;
}

interface ImportRecord {
  id: string;
  fileName: string;
  rowsImported: number;
  rowsSkipped: number;
  durationMs: number;
  createdAt: string;
}

const statusColors: Record<string, string> = {
  ACTIVE: 'text-green-400 bg-green-400/10',
  PAST_DUE: 'text-red-400 bg-red-400/10',
  CANCELED: 'text-gray-400 bg-gray-400/10',
  INCOMPLETE: 'text-yellow-400 bg-yellow-400/10',
};

export default function HealthAccountDetail() {
  const { id } = useParams<{ id: string }>();
  const [account, setAccount] = useState<AccountDetail | null>(null);
  const [billingEvents, setBillingEvents] = useState<BillingEvent[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const fetchAccount = () => {
    fetch(`/api/admin/health/accounts/${id}`)
      .then((r) => r.json())
      .then((data) => {
        setAccount(data.account);
        setBillingEvents(data.recentBillingEvents || []);
        setAuditEvents(data.recentAuditEvents || []);
        setImports(data.recentImports || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading account:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAccount();
  }, [id]);

  const updateAccount = async (data: Record<string, any>) => {
    setActionLoading('update');
    try {
      const res = await fetch(`/api/admin/health/accounts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        fetchAccount();
      }
    } finally {
      setActionLoading('');
    }
  };

  const revokeSessions = async () => {
    if (!confirm('Revoke all sessions for this account? The user will be logged out.')) return;
    setActionLoading('revoke');
    try {
      await fetch(`/api/admin/health/accounts/${id}/revoke-sessions`, { method: 'POST' });
      fetchAccount();
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Account not found</p>
        <Link to="/health/accounts" className="text-primary-400 hover:text-primary-300 mt-2 inline-block">
          Back to accounts
        </Link>
      </div>
    );
  }

  const isLocked = account.lockedUntil && new Date(account.lockedUntil) > new Date();

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to="/health/accounts" className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{account.email}</h1>
          <p className="text-gray-400">
            {account.displayName || 'No display name'} · Created {new Date(account.createdAt).toLocaleDateString()}
          </p>
        </div>
        <span
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            statusColors[account.subscriptionStatus] || ''
          }`}
        >
          {account.subscriptionStatus}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Details Card */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Account Details</h2>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-xs text-gray-400">Email</dt>
                <dd className="text-sm text-white flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  {account.email}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Email Verified</dt>
                <dd className="text-sm text-white">
                  {account.emailVerifiedAt
                    ? new Date(account.emailVerifiedAt).toLocaleString()
                    : <span className="text-yellow-400">Not verified</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Last Login</dt>
                <dd className="text-sm text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  {account.lastLoginAt
                    ? new Date(account.lastLoginAt).toLocaleString()
                    : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Active Sessions</dt>
                <dd className="text-sm text-white">{account._count.sessions}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Stripe Customer</dt>
                <dd className="text-sm text-white flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-500" />
                  {account.stripeCustomerId || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Plan / Period End</dt>
                <dd className="text-sm text-white">
                  {account.subscriptionPlan || '—'}
                  {account.subscriptionCurrentPeriodEnd && (
                    <span className="text-gray-400 ml-1">
                      (ends {new Date(account.subscriptionCurrentPeriodEnd).toLocaleDateString()})
                    </span>
                  )}
                </dd>
              </div>
            </dl>

            {isLocked && (
              <div className="mt-4 p-3 bg-red-500/10 rounded-lg flex items-center gap-3">
                <Shield className="w-5 h-5 text-red-400" />
                <div className="flex-1">
                  <p className="text-sm text-red-400 font-medium">Account Locked</p>
                  <p className="text-xs text-gray-400">
                    {account.failedLoginCount} failed attempts · Locked until{' '}
                    {new Date(account.lockedUntil!).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => updateAccount({ lockedUntil: null })}
                  disabled={actionLoading === 'update'}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  Unlock
                </button>
              </div>
            )}
          </div>

          {/* Usage Stats */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Data & Usage</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                <Upload className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-white">{account._count.ouraImports}</p>
                <p className="text-xs text-gray-400">Imports</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                <Brain className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-white">{account._count.ouraGrokAnalyses}</p>
                <p className="text-xs text-gray-400">AI Analyses</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-white">{account._count.ouraDailySummaries}</p>
                <p className="text-xs text-gray-400">Daily Summaries</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-sm text-gray-400">Sleep Sessions</p>
                <p className="text-lg font-bold text-white">{account._count.ouraSleepSessions}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-sm text-gray-400">Workouts</p>
                <p className="text-lg font-bold text-white">{account._count.ouraWorkouts}</p>
              </div>
            </div>
          </div>

          {/* Recent Imports */}
          {imports.length > 0 && (
            <div className="bg-gray-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Recent Imports</h2>
              <div className="space-y-2">
                {imports.map((imp) => (
                  <div key={imp.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                    <div>
                      <p className="text-sm text-white">{imp.fileName}</p>
                      <p className="text-xs text-gray-400">
                        {imp.rowsImported} imported, {imp.rowsSkipped} skipped · {imp.durationMs}ms
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(imp.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit Log */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Audit Log</h2>
              <Link
                to={`/health/audit?accountId=${id}`}
                className="text-sm text-primary-400 hover:text-primary-300"
              >
                View all
              </Link>
            </div>
            <div className="space-y-2">
              {auditEvents.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No audit events</p>
              ) : (
                auditEvents.map((event) => (
                  <div key={event.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                    <div>
                      <p className="text-sm text-white">{event.action}</p>
                      <p className="text-xs text-gray-400">
                        {event.resource}{event.resourceId ? ` (${event.resourceId.slice(0, 8)})` : ''} ·{' '}
                        <span className={event.status === 'SUCCESS' ? 'text-green-400' : 'text-red-400'}>
                          {event.status}
                        </span>
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(event.createdAt).toLocaleString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-6">
          {/* Admin Actions */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Admin Actions</h2>
            <div className="space-y-3">
              {/* Override subscription status */}
              <div>
                <label className="text-xs text-gray-400 block mb-1">Override Subscription</label>
                <select
                  value={account.subscriptionStatus}
                  onChange={(e) => updateAccount({ subscriptionStatus: e.target.value })}
                  disabled={actionLoading === 'update'}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="PAST_DUE">PAST_DUE</option>
                  <option value="CANCELED">CANCELED</option>
                  <option value="INCOMPLETE">INCOMPLETE</option>
                </select>
              </div>

              {/* Revoke sessions */}
              <button
                onClick={revokeSessions}
                disabled={actionLoading === 'revoke'}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-yellow-600/20 text-yellow-400 rounded-lg hover:bg-yellow-600/30 text-sm disabled:opacity-50"
              >
                <LogOut className="w-4 h-4" />
                {actionLoading === 'revoke' ? 'Revoking...' : 'Revoke All Sessions'}
              </button>

              {/* Unlock if locked */}
              {isLocked && (
                <button
                  onClick={() => updateAccount({ lockedUntil: null })}
                  disabled={actionLoading === 'update'}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 text-sm disabled:opacity-50"
                >
                  <Shield className="w-4 h-4" />
                  Unlock Account
                </button>
              )}
            </div>
          </div>

          {/* Billing Events */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Billing Events</h2>
            <div className="space-y-2">
              {billingEvents.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No billing events</p>
              ) : (
                billingEvents.map((event) => (
                  <div key={event.id} className="py-2 border-b border-gray-700 last:border-0">
                    <p className="text-sm text-white">{event.type}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(event.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Info */}
          {account.subscriptionStatus === 'PAST_DUE' && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="text-sm font-medium text-red-400">Payment Failed</h3>
              </div>
              <p className="text-xs text-gray-400">
                This account has a failed payment. Stripe will retry automatically. You can also override the status to ACTIVE for a grace period.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
